import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import { useAction } from "wasp/client/operations";
import { deleteWalletTransfer, updateWalletTransferStatus } from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import useTranslation from "../client/hooks/useTranslation";

interface Transfer {
  id: string;
  date: Date | string;
  grossAmount: number;
  splitPercent: number;
  netAmount: number;
  status: "pending" | "approved" | "rejected";
  notes: string | null;
  /** Non nul = détecté par la sync (transfert vers le wallet, net estimé). */
  source?: string | null;
  account: { id: string; name: string; currency: string };
}

interface WalletTransfersTableProps {
  transfers: Transfer[];
  onMutated: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  approved: "bg-primary/15 text-primary border-primary/30",
  pending: "bg-amber-400/15 text-amber-500 border-amber-400/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
};

export function WalletTransfersTable({ transfers, onMutated }: WalletTransfersTableProps) {
  const t = useTranslation();
  const w = t.propFirmManager.wallet;
  const deleteTransfer = useAction(deleteWalletTransfer);
  const updateStatus = useAction(updateWalletTransferStatus);

  const handleDelete = async (transferId: string) => {
    if (!window.confirm(w.deleteTransferConfirm)) return;
    await deleteTransfer({ transferId });
    onMutated();
  };

  // Part reçue corrigée par l'utilisateur : le net est recalculé côté serveur.
  const handleSplitChange = async (tr: Transfer, raw: string) => {
    const splitPercent = parseFloat(raw);
    if (!Number.isFinite(splitPercent) || splitPercent <= 0 || splitPercent > 100) return;
    if (splitPercent === tr.splitPercent) return;
    await updateStatus({ transferId: tr.id, status: tr.status, splitPercent });
    onMutated();
  };

  const handleStatusChange = async (transferId: string, status: string) => {
    await updateStatus({
      transferId,
      status: status as "pending" | "approved" | "rejected",
    });
    onMutated();
  };

  if (transfers.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-4 text-center">{w.noTransfers}</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground border-b border-border">
            <th className="text-left pb-2 pr-3 font-medium whitespace-nowrap">{w.colDate}</th>
            <th className="text-left pb-2 pr-3 font-medium whitespace-nowrap">{w.colAccount}</th>
            <th className="text-right pb-2 pr-3 font-medium">{w.colGross}</th>
            <th className="text-right pb-2 pr-3 font-medium">{w.colSplit}</th>
            <th className="text-right pb-2 pr-3 font-medium">{w.colNet}</th>
            <th className="text-left pb-2 pr-3 font-medium whitespace-nowrap">{w.colStatus}</th>
            <th className="text-right pb-2 font-medium">{w.colActions}</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((tr) => (
            <tr key={tr.id} className="border-b border-border">
              <td className="py-2.5 pr-3 font-mono whitespace-nowrap">
                {format(new Date(tr.date), "dd MMM yyyy")}
              </td>
              <td className="py-2.5 pr-3 max-w-[120px]">
                <span className="block truncate">{tr.account.name}</span>
                {tr.source && (
                  <span className="text-[10px] text-muted-foreground" title={w.detectedTitle}>
                    {w.detectedTag}
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-right font-mono whitespace-nowrap">${tr.grossAmount.toFixed(2)}</td>
              <td className="py-2.5 pr-3 text-right font-mono text-muted-foreground whitespace-nowrap">
                {tr.status === "pending" ? (
                  <input
                    key={`${tr.id}-${tr.splitPercent}`}
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    defaultValue={tr.splitPercent}
                    aria-label={w.splitEditLabel}
                    title={w.splitEditLabel}
                    onBlur={(e) => handleSplitChange(tr, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    className="w-14 h-6 rounded border border-input bg-background px-1 text-right text-xs font-mono"
                  />
                ) : (
                  <>{tr.splitPercent}%</>
                )}
              </td>
              <td className="py-2.5 pr-3 text-right font-mono text-pnlPositive font-semibold whitespace-nowrap">
                ${tr.netAmount.toFixed(2)}
              </td>
              <td className="py-2.5 pr-3">
                <Select value={tr.status} onValueChange={(v) => handleStatusChange(tr.id, v)}>
                  <SelectTrigger
                    className={`h-6 text-xs border px-2 w-24 ${STATUS_BADGE[tr.status]}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{w.statusPending}</SelectItem>
                    <SelectItem value="approved">{w.statusApproved}</SelectItem>
                    <SelectItem value="rejected">{w.statusRejected}</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="py-2.5 text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(tr.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
